import {
  PHASE_IDS_BASE,
  PHASE_IDS_ELBB_EXTRAS,
  PHASE_IDS_PREMIO_BB_EXTRAS,
} from 'utils/constants';
import { getProduto, Produto } from './produto-env';

export { getProduto };
export type { Produto };

/**
 * Shape genérica dos IDs de fase — base + extras do produto.
 * Chaves ausentes retornam undefined; useIsDateActive(undefined) → false.
 */
export type PhaseIds = Readonly<Record<string, string | undefined>>;

/**
 * Subconjunto de ActivePhases usado por shouldShowSimpleHeader.
 * ActivePhases (hooks/useControlDate) é um superconjunto estrutural deste tipo,
 * portanto é diretamente atribuível sem cast.
 */
export interface PhasesForHeader {
  conexaoPreRegistration: boolean;
  conexaoManagerApproval: boolean;
  conexaoRegistrationPeriod: boolean;
  elbbPreRegistration: boolean;
  elbbInscriptionPeriod: boolean;
  elbbConfirmation: boolean;
}

export interface ProdutoConfig {
  produto: Produto;
  titulo: 'Prêmio BB' | 'Prêmio ELBB';
  /** IDs de fase do produto — base + extras. Alimentado em T2. */
  phaseIds: PhaseIds;
  // tutorialIds: TutorialIds — adicionado na T6 (YAGNI por ora)
  pagesWithCustomHeader: string[];
  /**
   * Retorna true se o SimpleHeader deve ser exibido (verifica fase e pathname).
   * O app-container também verifica isAuthenticated.
   */
  shouldShowSimpleHeader: (phases: PhasesForHeader, pathname: string) => boolean;
}

// ---------------------------------------------------------------------------
// Páginas que possuem header customizado próprio (sem SimpleHeader)
// ---------------------------------------------------------------------------

const PAGES_WITH_CUSTOM_HEADER_BB: string[] = [
  '/confirmacao-premio',
  '/confirmacao-participacao-premio',
  '/listagem-inscricao-elbb',
  '/home-public',
  '/home-elbb',
  '/iniciativas',
  '/iniciativas-elbb',
  '/minha-jornada',
  '/minha-jornada-elbb',
  '/minhas-iniciativas',
  '/listagem-inscricao-premio',
  '/regulamento',
  '/pre-inscricao',
  '/listagem',
];

const PAGES_WITH_CUSTOM_HEADER_ELBB: string[] = [
  '/listagem-inscricao-elbb',
  '/home-public',
  '/home-elbb',
  '/iniciativas',
  '/iniciativas-elbb',
  '/minha-jornada',
  '/minha-jornada-elbb',
  '/pre-inscricao',
  '/listagem',
];

// ---------------------------------------------------------------------------
// Configurações por produto
// ---------------------------------------------------------------------------

const PRODUTO_CONFIGS: Record<Produto, ProdutoConfig> = {
  PREMIO_BB: {
    produto: 'PREMIO_BB',
    titulo: 'Prêmio BB',
    phaseIds: { ...PHASE_IDS_BASE, ...PHASE_IDS_PREMIO_BB_EXTRAS } as PhaseIds,
    pagesWithCustomHeader: PAGES_WITH_CUSTOM_HEADER_BB,
    shouldShowSimpleHeader: (phases, pathname) => {
      const isPreInscricaoAtiva =
        phases.conexaoPreRegistration ||
        phases.conexaoManagerApproval ||
        phases.conexaoRegistrationPeriod;
      return (
        isPreInscricaoAtiva &&
        !PAGES_WITH_CUSTOM_HEADER_BB.some((page) => pathname.startsWith(page))
      );
    },
  },

  PREMIO_ELBB: {
    produto: 'PREMIO_ELBB',
    titulo: 'Prêmio ELBB',
    phaseIds: { ...PHASE_IDS_BASE, ...PHASE_IDS_ELBB_EXTRAS } as PhaseIds,
    pagesWithCustomHeader: PAGES_WITH_CUSTOM_HEADER_ELBB,
    shouldShowSimpleHeader: (phases, pathname) =>
      !phases.elbbInscriptionPeriod &&
      (phases.elbbPreRegistration || phases.elbbConfirmation) &&
      !PAGES_WITH_CUSTOM_HEADER_ELBB.includes(pathname),
  },
};

// ---------------------------------------------------------------------------
// PRODUTO_CONFIG — avaliado uma vez no carregamento do módulo
// ---------------------------------------------------------------------------

export const PRODUTO_CONFIG: ProdutoConfig = PRODUTO_CONFIGS[getProduto()];
